import { matchesSearchText } from '@/utils/listSearch';
import type {
  AppVersionAreaManager,
  AppVersionCheckReport,
  AppVersionDepartment,
  AppVersionMember,
} from '../types/appVersionCheck.types';

function isMemberMatch(member: AppVersionMember, query: string): boolean {
  const searchableValues = [member.name, member.hrEmpId, member.mobile];
  return searchableValues.some((value) => matchesSearchText(value ?? '', query));
}

function filterAreaManager(
  areaManager: AppVersionAreaManager,
  query: string,
): AppVersionAreaManager | null {
  const members = areaManager.members.filter((member) => isMemberMatch(member, query));
  if (members.length === 0) return null;
  return { ...areaManager, members, memberCount: members.length };
}

function filterDepartment(
  department: AppVersionDepartment,
  query: string,
): AppVersionDepartment | null {
  const areaManagers = department.areaManagers.flatMap((areaManager) => {
    const matchingAreaManager = filterAreaManager(areaManager, query);
    return matchingAreaManager ? [matchingAreaManager] : [];
  });
  if (areaManagers.length === 0) return null;
  return { ...department, areaManagers };
}

export function filterAppVersionReport(
  report: AppVersionCheckReport,
  searchText: string,
): AppVersionCheckReport {
  const query = searchText.trim().toLowerCase();
  if (query === '') return report;

  const departments = report.departments.flatMap((department) => {
    const matchingDepartment = filterDepartment(department, query);
    return matchingDepartment ? [matchingDepartment] : [];
  });
  const areaManagers = departments.flatMap((department) => department.areaManagers);

  return {
    departments,
    totalDepartments: departments.length,
    totalAreaManagers: areaManagers.length,
    totalMembers: areaManagers.reduce((total, areaManager) => total + areaManager.memberCount, 0),
  };
}
